import { useCallback } from "react";

import { Action, Run, RunState, Stack, Module, RunType } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasRequiredAccess, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useHasStackReadAccess from "shared/Stack/access/useHasStackReadAccess";
import useHasModuleReadAccess from "shared/Module/access/useHasModuleReadAccess";
import useHasStackWriteAccess from "shared/Stack/access/useHasStackWriteAccess";
import useHasModuleWriteAccess from "shared/Module/access/useHasModuleWriteAccess";
import useTypedFlags from "hooks/useTypedFlags";

const STOP_ACCESS_TYPES = [Action.RunStop];
const STOP_BLOCKING_ACCESS_TYPES = [Action.RunStopBlocking];

const hasStopAccess = (viewer: AccountViewer, spaceId: string) =>
  hasRequiredAccess(STOP_ACCESS_TYPES, viewer, spaceId);

const hasStopBlockingAccess = (viewer: AccountViewer, spaceId: string) =>
  hasRequiredAccess(STOP_BLOCKING_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user can stop a run.
 *
 * For non-blocking runs (e.g. Proposed), users need either:
 * - Stack/Module read access
 * - RunStop permission
 *
 * For blocking runs (Tracked, Destroy, Task), users need either:
 * - Stack/Module write access
 * - RunStopBlocking permission
 *
 * @returns A function that takes a run and entity (Stack or Module) and returns an AccessResult
 */
const useCanStopRun = (): ((run: Run, entity: Stack | Module) => AccessResult) => {
  const { customAccessRoles } = useTypedFlags();
  const { viewer } = useTypedContext(AccountContext);
  const hasStackReadAccess = useHasStackReadAccess();
  const hasStackWriteAccess = useHasStackWriteAccess();
  const hasModuleReadAccess = useHasModuleReadAccess();
  const hasModuleWriteAccess = useHasModuleWriteAccess();

  return useCallback(
    (run: Run, entity: Stack | Module) => {
      if (
        run.state !== RunState.Initializing &&
        run.state !== RunState.Planning &&
        run.state !== RunState.Applying &&
        run.state !== RunState.Performing &&
        run.state !== RunState.Destroying
      ) {
        return { result: false };
      }

      const isBlocking =
        run.type === RunType.Tracked || run.type === RunType.Destroy || run.type === RunType.Task;

      const spaceId = entity.spaceDetails?.id || entity.space;

      if (!isBlocking) {
        if (entity.__typename === "Stack" && hasStackReadAccess(entity)) {
          return { result: true };
        }

        if (entity.__typename === "Module" && hasModuleReadAccess(entity)) {
          return { result: true };
        }

        if (customAccessRoles && hasStopAccess(viewer, spaceId)) {
          return { result: true };
        }

        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (entity.__typename === "Stack" && hasStackWriteAccess(entity)) {
        return { result: true };
      }

      if (entity.__typename === "Module" && hasModuleWriteAccess(entity)) {
        return { result: true };
      }

      if (customAccessRoles && hasStopBlockingAccess(viewer, spaceId)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [
      viewer,
      hasStackReadAccess,
      hasModuleReadAccess,
      hasStackWriteAccess,
      hasModuleWriteAccess,
      customAccessRoles,
    ]
  );
};

export default useCanStopRun;
